import { useGetSuggestedAddresses } from '../../features/address-book/hooks/use-get-suggested-addresses';
import { useFeatureFlags } from '../../hooks';
import { isLoggedIn } from '../../utils';
import { FieldType, FormFieldsGeneratorProps } from './form-fields-generator-types';
import { CollapseSection } from './fragments/collapsed-section';
import { transformAddressesToSugest } from './utils/get-addresses-for-autocomplete';
import { AddressSelectionState, AutocompleteTextField, Checkbox, Text, TextField } from '@dotcom/crown-ui';
import { Dropdown } from '@hallmark/web.core.forms.dropdown';
import React, { Fragment, useState } from 'react';

const SET_VALUE_OPTIONS = { shouldValidate: true, shouldDirty: true, shouldTouch: true };

export const FormFieldsGenerator = ({ selectedFormFields, register, setValue }: FormFieldsGeneratorProps) => {
  const [userInput, setUserInput] = useState('');
  const { IS_ADDRESS_AUTOCOMPLETE_ENABLED } = useFeatureFlags();
  const { suggestedAddressesData, isLoadingSuggestedAddressesData } = useGetSuggestedAddresses(userInput);
  const transformedSuggestedAddresses = transformAddressesToSugest(suggestedAddressesData?.data.suggestions ?? []);

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setUserInput(e.target.value);
  };

  const setSelectedSuggestion = (selectedSuggestion: AddressSelectionState) => {
    if (setValue && selectedSuggestion) {
      setValue('address_line_1', selectedSuggestion.streetLine, SET_VALUE_OPTIONS);
      setValue('city', selectedSuggestion.city, SET_VALUE_OPTIONS);
      setValue('state_code', selectedSuggestion.state, SET_VALUE_OPTIONS);
      setValue('zip', selectedSuggestion.zip, SET_VALUE_OPTIONS);
    }

    setUserInput('');
  };

  return (
    <Fragment>
      {selectedFormFields.map(
        (field, index) =>
          field !== undefined &&
          (field.typeOfField === FieldType.COLLAPSE && field.nestedFields !== undefined ? (
            <CollapseSection key={`collapse_${index}`}>
              {field.nestedFields.map((nestedField) => (
                <Fragment key={`${nestedField.name}_${index}`}>
                  <TextField
                    register={register(nestedField.name, nestedField.registerOptions)}
                    domId={nestedField.domId}
                    label={nestedField.label}
                    isError={nestedField.isError}
                    helperText={nestedField.helperText}
                    className={nestedField.addClass}
                    testId={nestedField.testId}
                    required={nestedField.required}
                    onChange={nestedField.onChange}
                    key={`textfield_${nestedField.name}_${index}`}
                  />
                </Fragment>
              ))}
            </CollapseSection>
          ) : (
            <Fragment key={`${field.name}_${index}`}>
              {field.typeOfField === FieldType.DROPDOWN && field.showDropdown && (
                <Dropdown
                  options={field.dropdownOptions}
                  label={field.label}
                  register={register(field.name, field.registerOptions)}
                  helperText={field.helperText}
                  isError={field.isError}
                  addClass={field.addClass}
                  testId={field.testId}
                  required={field.required}
                  key={`dropdown_${field.name}_${index}`}
                />
              )}
              {field.typeOfField === FieldType.TEXTFIELD && (
                <TextField
                  register={register(field.name, field.registerOptions)}
                  domId={field.domId}
                  label={field.label}
                  isError={field.isError}
                  helperText={field.helperText}
                  className={field.addClass}
                  testId={field.testId}
                  required={field.required}
                  onChange={field.onChange}
                  key={`textfield_${field.name}_${index}`}
                />
              )}
              {field.typeOfField === FieldType.AUTOCOMPLETE && IS_ADDRESS_AUTOCOMPLETE_ENABLED && (
                <>
                  <AutocompleteTextField
                    register={register(field.name, field.registerOptions)}
                    domId={field.domId}
                    label={field.label}
                    isError={field.isError}
                    helperText={field.helperText}
                    className={field.addClass}
                    testId={field.testId}
                    required={field.required}
                    onChange={onChange}
                    key={`textfield_${field.name}_${index}`}
                    enableAutocomplete={transformAddressesToSugest && transformAddressesToSugest.length > 0}
                    autocompleteData={transformedSuggestedAddresses}
                    onSuggestedAddressClick={setSelectedSuggestion}
                  />
                  {isLoadingSuggestedAddressesData && (
                    <Text color="textPrimary" as="span" data-testid="autocomplete-loading">
                      Loading addresses...
                    </Text>
                  )}
                </>
              )}
              {field.typeOfField === FieldType.CHECKBOX && isLoggedIn() && (
                <Checkbox
                  register={register(field.name, field.registerOptions)}
                  domId={field.domId}
                  helperText={field.helperText}
                  className={field.addClass}
                  testId={field.testId}
                  required={field.required}
                  key={`checkbox_${field.name}_${index}`}
                >
                  {field.label}
                </Checkbox>
              )}
            </Fragment>
          )),
      )}
    </Fragment>
  );
};
